import {
  Box,
  Fade,
  IconButton,
  ListItemIcon,
  ListItemText,
  makeStyles,
  MenuItem,
  MenuList,
  Popover,
  SvgIcon,
} from '@material-ui/core';
import useAttachFilesSummary from '@src/hooks/useAttachFilesSummary';
import { selectHasCallInActiveChat } from '@src/selectors/calls';
import { activeChatSelector } from '@src/selectors/chats';
import { RootState } from '@src/store/reduxstore';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { AntSwitch } from '../ChatMessageComposer/MoreOptions/AntSwitch';
import { ConfigIcon, threeDotsIcon, TransferIcon } from './SvgIcons';
import TransferDialogWrapper from './TransferDialogWrapper';

const MoreOptions: React.FC<{ disabled: boolean }> = ({ disabled }) => {
  const { formatMessage } = useIntl();

  const { popover, button, marginPopover, marginPopoverConfig } = useStyles();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElConfig, setAnchorElConfig] = useState<null | HTMLElement>(
    null
  );
  const [openTransferDialog, setOpenTransferDialog] = useState(false);

  const activeChat = useSelector((state: RootState) =>
    activeChatSelector(state)
  );
  const activeChatHasCall = useSelector(selectHasCallInActiveChat);

  const { toggleAttachFilesSummary, isAttachFilesSummaryEnabled } =
    useAttachFilesSummary();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenConfig = (
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    setAnchorElConfig(event.currentTarget);
  };

  const handleCloseConfig = () => {
    setAnchorElConfig(null);
  };

  return (
    <div>
      <IconButton className={button} onClick={handleClick} disabled={disabled}>
        <SvgIcon fontSize="small">{threeDotsIcon}</SvgIcon>
      </IconButton>
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        elevation={0}
        TransitionComponent={Fade}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        className={`${popover} ${marginPopover}`}
      >
        <MenuList>
          <MenuItem
            disabled={activeChatHasCall}
            onClick={() => setOpenTransferDialog(true)}
          >
            <ListItemIcon>{TransferIcon}</ListItemIcon>
            <ListItemText
              primary={formatMessage({
                id: 'AgentToolbar.actionButton.moreOptions.transfer.label',
              })}
            />
          </MenuItem>

          <MenuItem
            aria-owns={anchorElConfig ? 'mouse-over-popover' : undefined}
            aria-haspopup="true"
            onClick={handleOpenConfig}
          >
            <ListItemIcon>{ConfigIcon}</ListItemIcon>
            <ListItemText
              primary={formatMessage({
                id: 'AgentToolbar.actionButton.moreOptions.config.label',
              })}
            />
          </MenuItem>
        </MenuList>
      </Popover>

      <Popover
        id="mouse-over-popover"
        elevation={1}
        TransitionComponent={Fade}
        className={`${popover} ${marginPopoverConfig}`}
        open={Boolean(anchorElConfig)}
        anchorEl={anchorElConfig}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handleCloseConfig}
        disableRestoreFocus
      >
        <MenuList>
          <MenuItem>
            <ListItemText
              primary={formatMessage({
                id: 'AgentToolbar.actionButton.moreOptions.config.subMenu.imagePreview.label',
              })}
            />
            <Box ml={3} mr={0.5}>
              <AntSwitch
                disableRipple
                defaultChecked={isAttachFilesSummaryEnabled()}
                onChange={toggleAttachFilesSummary}
              />
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => {
              navigator.clipboard.writeText(activeChat?.id);
              handleCloseConfig();
            }}
          >
            <ListItemText
              primary={formatMessage({
                id: 'AgentToolbar.actionButton.moreOptions.config.subMenu.chatId.label',
              })}
            />
          </MenuItem>
        </MenuList>
      </Popover>
      {openTransferDialog && (
        <TransferDialogWrapper
          onTransfer={() => {
            setOpenTransferDialog(false);
            handleClose();
          }}
          onCancel={() => setOpenTransferDialog(false)}
        />
      )}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  popover: {
    '& .MuiPopover-paper': {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      gap: '16px',
      borderRadius: '16px',
      fontSize: '14px',
      fontFamily: 'Poppins',
      fontWeight: 400,
      lineHeight: '19.6px',
      textAlign: 'left',
      boxShadow:
        theme.palette.type === 'dark'
          ? '0px 0px 8px 0px rgba(0,0,0,0.6)'
          : '0px 0px 8px 0px #00000026',
    },
    '& .MuiMenuItem-root': {
      borderRadius: 0,
      '&:hover': {
        color: theme.palette.text.primary,
        backgroundColor: `${theme.palette.action.hover} !important`,
      },
    },
    '&.MuiListItem-button': {
      border: 0,
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.text.secondary,
      padding: '0 0 0 0px',
    },
    '& .MuiListItem-gutters ': {
      padding: '12px 16px !important',
    },
  },
  button: {
    color: theme.palette.text.primary,
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.grey[700]
        : theme.palette.grey[200],
    height: '36px',
    width: '36px',
    '&.Mui-disabled': {
      color: `${theme.palette.action.disabled} !important`,
      backgroundColor: `${theme.palette.action.disabledBackground} !important`,
    },
  },
  marginPopover: {
    marginTop: '17px',
  },

  marginPopoverConfig: {
    marginLeft: '-5px',
    marginTop: '-25px',
    '& .MuiListItem-gutters ': {
      padding: '5px 16px !important',
    },
  },
}));

export default MoreOptions;
